import { OppButton, OppInput } from '../components/ui/control';
import { buttonVariants } from '../components/ui/controlStyles';
import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { useSharedAuth } from '@litopia/shared';
import { getSupabase } from '@litopia/shared/supabase';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import {
  useCreateLetter, useLetterTemplates, useEnvelopeDesigns, useMyLetters,
} from '../hooks/useOpp';
import {
  Send, Save, PenLine, Clock, MapPin, CheckCircle2, Wifi, Truck,
  FileText, Mail, ThumbsUp, Reply, ChevronDown, ChevronRight,
  Eye, EyeOff, AlarmClock, PanelRightOpen, PanelRightClose,
  Droplets,
} from 'lucide-react';
const LetterEditor = lazy(() => import('../components/editor/LetterEditor'));
import { useCopy } from '../copy';
import { WriteSkeleton } from '../components/ui/Skeleton';
import type { OppLetter } from '@/types/opp';
import { sendLetterSchema } from '../lib/schemas';
import { toast } from 'sonner';
import { logError } from '../utils/errorHandler';
import { SemanticState } from '../components/semantic/SemanticState';
import { SemanticFeedback } from '../components/semantic/SemanticFeedback';
import { ONE_DAY_MS, AUTO_SAVE_INTERVAL_MS, DRAFT_SAVED_TOAST_DURATION_MS } from '../lib/constants';


type LetterType = 'normal' | 'future';
type StyleId = 'classic' | 'modern' | 'vintage' | 'elegant';
type Visibility = 'private' | 'public';
type DeliveryMode = 'online' | 'offline';

const STYLES = [
  { id: 'classic' as StyleId, preview: 'opp-letter-preview-classic' },
  { id: 'modern' as StyleId, preview: 'opp-letter-preview-modern' },
  { id: 'vintage' as StyleId, preview: 'opp-letter-preview-vintage' },
  { id: 'elegant' as StyleId, preview: 'opp-letter-preview-elegant' },
] as const;

export default function WritePage() {
  const { user, loading } = useSharedAuth();
  const { t } = useCopy();
  const createLetter = useCreateLetter();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedLetterType = searchParams.get('type');
  const requestedLetterTypeAtMount = useRef(requestedLetterType);

  const [letterType, setLetterType] = useState<LetterType>(requestedLetterType === 'future' ? 'future' : 'normal');
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('online');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [style, setStyle] = useState<StyleId>('classic');
  const [recipientCode, setRecipientCode] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('private');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showEnvelopes, setShowEnvelopes] = useState(false);
  const [selectedEnvelopeId, setSelectedEnvelopeId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [validating, setValidating] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<{ kind: 'success' | 'warning' | 'error'; title: string; description: string } | null>(null);
  const [deliveryWarning, setDeliveryWarning] = useState<string | null>(null);

  const { data: templates } = useLetterTemplates();
  const { data: envelopeDesigns } = useEnvelopeDesigns();
  const { data: drafts } = useMyLetters('drafts');

  useEffect(() => {
    const replyTo = searchParams.get('replyTo');
    if (replyTo) setReplyToId(replyTo);
  }, [searchParams]);

  useEffect(() => {
    if (requestedLetterType === 'normal' || requestedLetterType === 'future') {
      setLetterType(requestedLetterType);
    }
  }, [requestedLetterType]);

  useEffect(() => {
    const saved = localStorage.getItem('opp-draft');
    if (saved) {
      try {
        const draft = JSON.parse(saved);
        if (draft.content) setContent(draft.content);
        if (draft.title) setTitle(draft.title);
        if (draft.style) setStyle(draft.style);
        if (requestedLetterTypeAtMount.current !== 'normal' && requestedLetterTypeAtMount.current !== 'future' && draft.letterType) {
          setLetterType(draft.letterType);
        }
        if (draft.recipientCode) setRecipientCode(draft.recipientCode);
        if (draft.scheduledDate) setScheduledDate(draft.scheduledDate);
        if (draft.isAnonymous !== undefined) setIsAnonymous(draft.isAnonymous);
        if (draft.deliveryMode === 'online' || draft.deliveryMode === 'offline') setDeliveryMode(draft.deliveryMode);
      } catch {
        // Ignore malformed draft data
      }
    }
  }, []);

  const selectLetterType = (nextType: LetterType) => {
    setLetterType(nextType);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('type', nextType);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const selectDeliveryMode = (nextMode: DeliveryMode) => {
    setDeliveryMode(nextMode);
    if (nextMode === 'online') setRecipientCode('');
  };

  // Auto-save draft to localStorage every 30s
  useEffect(() => {
    const timer = setInterval(() => {
      if (content.trim()) {
        localStorage.setItem('opp-draft', JSON.stringify({ title, content, style, letterType, deliveryMode, recipientCode, scheduledDate, isAnonymous }));
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), DRAFT_SAVED_TOAST_DURATION_MS);
      }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [title, content, style, letterType, deliveryMode, recipientCode, scheduledDate, isAnonymous]);

  const LETTER_TYPES = [
    { id: 'normal' as LetterType, label: t('write.type.normal'), icon: PenLine, desc: t('write.type.normal.desc') },
    { id: 'future' as LetterType, label: t('write.type.future'), icon: Clock, desc: t('write.type.future.desc') },
  ];

  const STYLE_LABELS: Record<StyleId, { label: string; desc: string }> = {
    classic: { label: t('write.style.classic'), desc: t('write.style.classic.desc') },
    modern: { label: t('write.style.modern'), desc: t('write.style.modern.desc') },
    vintage: { label: t('write.style.vintage'), desc: t('write.style.vintage.desc') },
    elegant: { label: t('write.style.elegant'), desc: t('write.style.elegant.desc') },
  };

  if (loading) return <WriteSkeleton />;
  if (!user) return <Navigate to="/login" />;

  const selectedStyle = STYLES.find((s) => s.id === style) ?? STYLES[0];

  const handleSaveDraft = async () => {
    setActionFeedback(null);
    if (!content.trim()) {
      setFieldError('请先写下信件内容，再保存草稿。');
      return;
    }
    setFieldError(null);
    try {
      await createLetter.mutateAsync({
        id: draftId || undefined,
        title: title || undefined,
        content,
        style,
        status: 'draft',
        type: letterType,
        recipient_op_code: deliveryMode === 'offline' ? recipientCode || undefined : undefined,
        envelope_id: selectedEnvelopeId || undefined,
        reply_to: replyToId || undefined,
        is_anonymous: isAnonymous,
        metadata: { delivery_mode: deliveryMode },
      });
      setActionFeedback({ kind: 'success', title: '草稿已保存', description: '你可以稍后从草稿列表继续编辑。' });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('write.draft.saveFailed');
      setActionFeedback({ kind: 'error', title: '草稿保存失败', description: message });
      toast.error(message);
    }
  };

  const handleSend = async () => {
    setActionFeedback(null);
    setFieldError(null);
    const validation = sendLetterSchema.safeParse({
      content,
      title: title || undefined,
      recipientCode: recipientCode || undefined,
      scheduledDate: scheduledDate || undefined,
    });

    if (!validation.success) {
      const firstError = validation.error.errors[0];
      setFieldError(firstError.message);
      toast.error(firstError.message);
      return;
    }

    if (deliveryMode === 'offline' && !recipientCode) {
      setFieldError('线下投递需要填写收件人的 6 位 OP Code。');
      return;
    }

    if (letterType === 'future' && !scheduledDate) {
      setFieldError(t('write.schedule.dateRequired'));
      toast.error(t('write.schedule.dateRequired'));
      return;
    }

    if (recipientCode && recipientCode.length === 6) {
      try {
        setValidating(true);
        const sb = getSupabase();
        const { data: { session } } = await sb.auth.getSession();
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/opp-opcode-validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ action: 'validate', code: recipientCode }),
        });
        if (!res.ok) {
          const statusText = res.status === 404 ? t('write.opcode.notFound') : res.status >= 500 ? t('write.opcode.serverError') : t('write.opcode.networkError');
          const message = t('write.opcode.validationFailed').replace('%s', statusText);
          setFieldError(message);
          toast.error(message);
          return;
        }
        const result = await res.json();
        if (!result.valid) {
          const message = result.error || t('write.opcode.invalid');
          setFieldError(message);
          toast.error(message);
          return;
        }
      } catch (err) {
        logError(err, 'OP Code validation');
        setFieldError(t('write.opcode.unreachable'));
        toast.error(t('write.opcode.unreachable'));
        return;
      } finally {
        setValidating(false);
      }
    }

    const resolvedVisibility = deliveryMode === 'offline'
      ? 'private'
      : visibility;

    let letter: OppLetter | null = null;
    setDeliveryWarning(null);
    try {
      letter = await createLetter.mutateAsync({
        id: draftId || undefined,
        title: title || undefined,
        content,
        style,
        status: 'generated',
        type: letterType,
        visibility: resolvedVisibility,
        recipient_op_code: deliveryMode === 'offline' ? recipientCode || undefined : undefined,
        envelope_id: selectedEnvelopeId || undefined,
        reply_to: replyToId || undefined,
        scheduled_at: (letterType === 'future' && scheduledDate) ? scheduledDate : undefined,
        is_anonymous: isAnonymous,
        metadata: { delivery_mode: deliveryMode },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('write.send.failed');
      setActionFeedback({ kind: 'error', title: '信件发送失败', description: message });
      toast.error(message);
      return;
    }

    let hasDeliveryWarning = false;
    if (letter?.id && deliveryMode === 'offline') {
      const deliveryIssues: string[] = [];
      try {
        const sb = getSupabase();
        const { data: { session } } = await sb.auth.getSession();
        const barcodeResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/opp-barcode-generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ letter_id: letter.id }),
        });
        if (!barcodeResponse.ok) deliveryIssues.push('投递码生成');
      } catch (err) {
        logError(err, 'Barcode generation');
        deliveryIssues.push('投递码生成');
      }

      if (deliveryMode === 'offline' && recipientCode) {
        try {
          const sb = getSupabase();
          const { data: { session } } = await sb.auth.getSession();
          const courierResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/opp-courier-match`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
            body: JSON.stringify({ letter_id: letter.id }),
          });
          if (!courierResponse.ok) deliveryIssues.push('信使匹配');
        } catch (err) {
          logError(err, 'Courier matching');
          deliveryIssues.push('信使匹配');
        }
      }
      if (deliveryIssues.length > 0) {
        const warning = `信件已保存，但${deliveryIssues.join('、')}暂未完成。你可以先在信箱查看状态，稍后再试。`;
        hasDeliveryWarning = true;
        setDeliveryWarning(warning);
        setActionFeedback({ kind: 'warning', title: '信件已保存，投递准备仍在进行', description: warning });
      }
    }

    setSent(true);
    if (!hasDeliveryWarning) setActionFeedback({ kind: 'success', title: '信件已发送', description: '你可以在信件列表中查看投递状态。' });
    localStorage.removeItem('opp-draft');
  };

  if (sent) {
    return (
      <div className="flex min-h-[var(--litopia-layout-60vh)] flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[hsl(var(--opp-accent-soft))]">
          <CheckCircle2 className="h-8 w-8 text-[hsl(var(--opp-accent-strong))]" />
        </div>
        <h2 className="text-2xl font-serif font-bold text-[hsl(var(--opp-ink))]">{deliveryWarning ? '信件已保存' : t('write.success.title')}</h2>
        <p className="text-sm text-[hsl(var(--opp-accent-strong))]">{deliveryWarning ?? t('write.success.desc')}</p>
        {deliveryWarning && <SemanticFeedback kind="warning" title="投递状态待确认" className="max-w-md text-left">打开信箱可以查看最新的投递时间线。</SemanticFeedback>}
        <div className="flex gap-3 mt-2">
          <Link to="/letters" className={buttonVariants({ variant: 'primary', className: 'inline-flex min-h-11 items-center rounded-full px-5 py-2 text-sm font-medium shadow' })}>
            {t('write.success.view')}
          </Link>
          <OppButton
            onClick={() => { setSent(false); setTitle(''); setContent(''); setRecipientCode(''); setStyle('classic'); setLetterType('normal'); setVisibility('private'); setReplyToId(null); setDraftId(null); setSelectedEnvelopeId(null); setScheduledDate(''); setIsAnonymous(false); setFieldError(null); setActionFeedback(null); setDeliveryWarning(null); }}
            className="min-h-11 rounded-full border border-[hsl(var(--opp-border))] px-5 py-2 text-sm font-medium text-[hsl(var(--opp-accent-strong))] hover:bg-[hsl(var(--opp-surface-raised))] transition-colors"
          >
            {t('write.success.another')}
          </OppButton>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[var(--litopia-layout-100dvh)] bg-letter-paper">
      {/* ── MAIN CONTENT ── */}
      <div className="flex-1 min-w-0 mx-auto max-w-[var(--litopia-layout-1400px)] px-4 md:px-8 lg:px-12 pb-48 md:pb-8 pt-2 space-y-5">

        {/* Auto-save indicator */}
        <div className="flex items-center justify-between text-xs text-[hsl(var(--opp-muted))] h-5">
          <span className={`transition-opacity duration-500 ${draftSaved ? 'opacity-100' : 'opacity-0'}`}>
            {t('write.draft.autoSaved')}
          </span>
          {/* Desktop: sidebar toggle */}
          <OppButton
            onClick={() => setSidebarOpen((v) => !v)}
            aria-expanded={sidebarOpen}
            className="hidden min-h-11 md:flex items-center gap-1 px-2 text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors"
          >
            {sidebarOpen
              ? <PanelRightClose className="h-4 w-4" />
              : <PanelRightOpen className="h-4 w-4" />}
            {t('write.nav.drafts')}
          </OppButton>
        </div>

        {/* Letter type tabs */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">{t('write.type.label')}</p>
          <div className="grid grid-cols-2 gap-2">
            {LETTER_TYPES.map((lt) => {
              const Icon = lt.icon;
              const active = letterType === lt.id;
              return (
                <OppButton key={lt.id} onClick={() => selectLetterType(lt.id)} aria-pressed={active}
                  className={`opp-write-choice min-h-11 rounded-xl border-2 p-3 text-left transition-all ${active ? 'border-[hsl(var(--opp-accent))] bg-[hsl(var(--opp-surface-raised))] shadow-sm' : 'border-[hsl(var(--opp-border))] hover:border-[hsl(var(--opp-border))]'}`}>
                  <Icon className={`h-5 w-5 mb-1.5 ${active ? 'text-[hsl(var(--opp-accent-strong))]' : 'text-[hsl(var(--opp-muted))]'}`} />
                  <p className={`text-xs font-semibold leading-tight ${active ? 'text-[hsl(var(--opp-ink))]' : 'text-[hsl(var(--opp-accent-strong))]'}`}>{lt.label}</p>
                  <p className="mt-0.5 text-caption text-[hsl(var(--opp-muted))] leading-tight">{lt.desc}</p>
                </OppButton>
              );
            })}
          </div>
          <Link to="/drift/compose" className="mt-2 flex min-h-11 items-center gap-1.5 text-xs text-[hsl(var(--opp-muted))] transition-colors hover:text-[hsl(var(--opp-accent-strong))]">
            <Droplets className="h-3.5 w-3.5" />
            想写漂流瓶？去漂流 →
          </Link>
        </div>

        {/* Delivery mode is an explicit journey choice: in-app or physical courier. */}
        <div aria-labelledby="write-delivery-mode-label">
          <p id="write-delivery-mode-label" className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">投递方式</p>
          <div className="grid grid-cols-2 gap-2">
            {([
              { id: 'online' as DeliveryMode, label: '线上信件', desc: '直接出现在站内信箱', Icon: Wifi },
              { id: 'offline' as DeliveryMode, label: '线下投递', desc: '由信使送到收件人的 OP Code', Icon: Truck },
            ] as const).map(({ id, label, desc, Icon }) => {
              const active = deliveryMode === id;
              return (
                <OppButton key={id} type="button" onClick={() => selectDeliveryMode(id)} aria-pressed={active}
                  className={`min-h-11 rounded-xl border-2 p-3 text-left transition-all ${active ? 'border-[hsl(var(--opp-accent))] bg-[hsl(var(--opp-surface-raised))] shadow-sm' : 'border-[hsl(var(--opp-border))] hover:border-[hsl(var(--opp-accent-soft))]'}`}>
                  <Icon className={`mb-1.5 h-5 w-5 ${active ? 'text-[hsl(var(--opp-accent-strong))]' : 'text-[hsl(var(--opp-muted))]'}`} />
                  <p className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{label}</p>
                  <p className="mt-0.5 text-caption text-[hsl(var(--opp-muted))]">{desc}</p>
                </OppButton>
              );
            })}
          </div>
        </div>

        {/* Style selector */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">{t('write.style.label')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {STYLES.map((s) => (
              <OppButton key={s.id} onClick={() => setStyle(s.id)}
                className={`rounded-xl border-2 overflow-hidden transition-all min-h-[var(--litopia-layout-44px)] ${style === s.id ? 'border-[hsl(var(--opp-accent))] shadow-md ring-2 ring-[hsl(var(--opp-accent)/.20)]' : 'border-[hsl(var(--opp-border))] hover:border-[hsl(var(--opp-border))]'}`}>
                <div className={`opp-letter-preview h-12 w-full border-b ${s.preview}`} />
                <div className="opp-write-style-label px-1.5 py-1.5 text-center">
                  <p className={`text-label font-semibold ${style === s.id ? 'text-[hsl(var(--opp-accent-strong))]' : 'text-[hsl(var(--opp-muted))]'}`}>{STYLE_LABELS[s.id].label}</p>
                  <p className="text-caption-compact text-[hsl(var(--opp-muted))]">{STYLE_LABELS[s.id].desc}</p>
                </div>
              </OppButton>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-[hsl(var(--opp-border))]" />

        {/* Reply indicator */}
        {replyToId && (
          <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-3 py-2 text-xs text-[hsl(var(--opp-accent-strong))]">
            <Reply className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--opp-muted))]" />
            <span>{t('write.reply.label')} <span className="font-mono font-semibold">{replyToId.slice(0, 8)}…</span></span>
            <OppButton onClick={() => setReplyToId(null)} className="ml-auto text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-accent-strong))] transition-colors">{t('write.reply.cancel')}</OppButton>
          </div>
        )}

        {/* Title + Editor */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">{t('write.field.title')}</p>
          <OppInput
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('write.title.placeholder')}
            className="opp-write-input mb-3 w-full rounded-xl border-2 border-[hsl(var(--opp-border))] px-4 py-3 font-serif text-xl sm:text-2xl placeholder:text-[hsl(var(--opp-muted))] focus:border-[hsl(var(--opp-accent))] focus:outline-none transition-all"
          />
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">{t('write.field.content')}</p>
          <Suspense
            fallback={
              <div className="flex min-h-[var(--litopia-layout-220px)] items-center justify-center rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))]/40 text-sm text-[hsl(var(--opp-accent-strong))]">
                {t('common.loading')}
              </div>
            }
          >
            <LetterEditor
              content={content}
              onChange={setContent}
              paperClass={selectedStyle.id}
              placeholder={t('write.content.placeholder')}
            />
          </Suspense>
          {fieldError && <SemanticFeedback kind="error" title="请检查信件内容" className="mt-3">{fieldError}</SemanticFeedback>}
        </div>

        {/* Divider */}
        <div className="border-t border-[hsl(var(--opp-border))]" />

        {/* More Options (collapsible) */}
        <div>
          <OppButton
            data-opp-write-more-toggle
            onClick={() => setShowMore((v) => !v)}
            className="flex min-h-11 items-center gap-2 text-sm font-semibold text-[hsl(var(--opp-accent-strong))] hover:text-[hsl(var(--opp-ink))] transition-colors"
          >
            {showMore ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {t('write.options.more')}
            {(recipientCode || selectedEnvelopeId) && (
              <span className="ml-1 rounded-full bg-[hsl(var(--opp-accent))] px-1.5 py-0.5 text-caption-compact font-bold text-[hsl(var(--opp-accent-foreground))]">{t('write.options.configured')}</span>
            )}
          </OppButton>

          {showMore && (
            <div className="opp-write-panel mt-3 space-y-4 rounded-xl border border-[hsl(var(--opp-border))] p-4">

              {/* Recipient OP Code */}
              {deliveryMode === 'offline' && (
                <div>
                  <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">
                    <MapPin className="h-3.5 w-3.5" />
                    {t('write.recipient.label')}
                  </label>
                  <OppInput
                    type="text"
                    value={recipientCode}
                    onChange={(e) => setRecipientCode(e.target.value.toUpperCase().slice(0, 6))}
                    placeholder={t('write.recipient.placeholder')}
                    maxLength={6}
                    className="opp-write-input w-full rounded-xl border-2 border-[hsl(var(--opp-border))] px-4 py-2.5 font-mono text-sm tracking-widest placeholder:text-[hsl(var(--opp-muted))] placeholder:tracking-normal focus:border-[hsl(var(--opp-accent))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent)/.20)]"
                  />
                  {fieldError && recipientCode && <p className="mt-1 text-xs text-feedback-error" role="alert">{fieldError}</p>}
                  <div className="mt-1 flex items-center justify-between text-xs">
                    <p className="text-[hsl(var(--opp-muted))]">{t('write.recipient.hint')}</p>
                    {recipientCode && (
                      <p className={`font-mono ${recipientCode.length === 6 ? 'text-feedback-success' : 'text-[hsl(var(--opp-muted))]'}`}>
                        {recipientCode.length}/6
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Template selector */}
              <div>
                <OppButton onClick={() => setShowTemplates(!showTemplates)} aria-expanded={showTemplates}
                  className="flex min-h-11 items-center gap-1.5 px-1 text-xs font-semibold text-[hsl(var(--opp-accent-strong))] hover:text-[hsl(var(--opp-ink))] transition-colors">
                  <FileText className="h-3.5 w-3.5" />
                  {showTemplates ? t('write.template.hide') : t('write.template.show')}
                </OppButton>
                {showTemplates && templates && templates.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {templates.map((tpl: { id: string; name: string; description?: string; content_template?: string; style?: string }) => (
                      <OppButton key={tpl.id}
                        onClick={() => { if (tpl.content_template) setContent(tpl.content_template); if (tpl.style) setStyle(tpl.style as StyleId); setShowTemplates(false); }}
                        className="letter-card min-h-11 rounded-xl p-3 text-left hover:shadow-md transition-all">
                        <p className="text-xs font-semibold text-[hsl(var(--opp-ink))] truncate">{tpl.name}</p>
                        {tpl.description && <p className="text-caption text-[hsl(var(--opp-accent-strong))] mt-0.5 line-clamp-2">{tpl.description}</p>}
                      </OppButton>
                    ))}
                  </div>
                )}
                {showTemplates && (!templates || templates.length === 0) && (
                  <SemanticState compact illustration="empty-template" kind="empty" title={t('write.template.empty')} />
                )}
              </div>

              {/* Envelope selector */}
              <div>
                <OppButton onClick={() => setShowEnvelopes(!showEnvelopes)} aria-expanded={showEnvelopes}
                  className="flex min-h-11 items-center gap-1.5 px-1 text-xs font-semibold text-[hsl(var(--opp-accent-strong))] hover:text-[hsl(var(--opp-ink))] transition-colors">
                  <Mail className="h-3.5 w-3.5" />
                  {showEnvelopes ? t('write.envelope.hide') : t('write.envelope.show')}
                  {selectedEnvelopeId && (
                    <span className="ml-1 rounded-full bg-[hsl(var(--opp-accent))] px-1.5 py-0.5 text-caption-compact font-bold text-[hsl(var(--opp-accent-foreground))]">{t('write.envelope.selected')}</span>
                  )}
                </OppButton>
                {showEnvelopes && (
                  <div className="mt-2">
                    {envelopeDesigns && envelopeDesigns.length > 0 ? (
                      <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-none">
                        {envelopeDesigns.map((env) => {
                          const isSelected = selectedEnvelopeId === env.id;
                          return (
                            <OppButton key={env.id} onClick={() => setSelectedEnvelopeId(isSelected ? null : env.id)} aria-pressed={isSelected}
                              className={`min-h-11 shrink-0 w-28 rounded-xl border-2 overflow-hidden transition-all ${isSelected ? 'border-[hsl(var(--opp-accent))] shadow-md ring-2 ring-[hsl(var(--opp-accent)/.20)]' : 'border-[hsl(var(--opp-border))] hover:border-[hsl(var(--opp-border))]'}`}>
                              <div className="h-16 w-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--opp-surface-raised))] to-[hsl(var(--opp-accent-soft))]">
                                {env.thumbnail_url || env.image_url ? (
                                  <img src={env.thumbnail_url || env.image_url!} alt={env.theme || ''} className="h-full w-full object-cover" />
                                ) : (
                                  <Mail className="h-6 w-6 text-[hsl(var(--opp-accent-strong))] opacity-60" />
                                )}
                              </div>
                              <div className="opp-write-style-label px-2 py-1.5">
                                <p className={`truncate text-label font-semibold ${isSelected ? 'text-[hsl(var(--opp-accent-strong))]' : 'text-[hsl(var(--opp-accent-strong))]'}`}>
                                  {env.theme || env.description || t('write.envelope.default')}
                                </p>
                                <div className="flex items-center justify-between text-caption-compact text-[hsl(var(--opp-muted))]">
                                  <span className="flex items-center gap-0.5">
                                    <ThumbsUp className="h-2.5 w-2.5" />{env.vote_count ?? 0}
                                  </span>
                                  <span>{env.type === 'city' ? t('write.envelope.typeCity') : t('write.envelope.typeCampus')}</span>
                                </div>
                              </div>
                            </OppButton>
                          );
                        })}
                      </div>
                    ) : (
                      <SemanticState compact illustration="empty-envelope" kind="empty" title={t('write.envelope.empty')} />
                    )}
                  </div>
                )}
              </div>

              {/* Visibility is only meaningful for the in-app journey. */}
              {deliveryMode === 'online' && <div>
                  <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">
                    <Eye className="h-3.5 w-3.5" />
                    {t('write.visibility.label')}
                  </label>
                  <div className="flex gap-2">
                    {([
                      { id: 'private' as Visibility, labelKey: 'write.visibility.private', icon: EyeOff },
                      { id: 'public' as Visibility, labelKey: 'write.visibility.public', icon: Eye },
                    ] as const).map((opt) => {
                      const Icon = opt.icon;
                      const active = visibility === opt.id;
                      return (
                        <OppButton key={opt.id} onClick={() => setVisibility(opt.id)}
                          className={`flex min-h-11 min-w-11 items-center gap-1.5 rounded-lg border-2 px-3 py-2 text-xs font-medium transition-all ${active ? 'border-[hsl(var(--opp-accent))] bg-[hsl(var(--opp-surface-raised))] text-[hsl(var(--opp-ink))]' : 'border-[hsl(var(--opp-border))] text-[hsl(var(--opp-muted))] hover:border-[hsl(var(--opp-border))]'}`}>
                          <Icon className="h-3.5 w-3.5" />
                          {t(opt.labelKey)}
                        </OppButton>
                      );
                    })}
                  </div>
              </div>}

              {/* Scheduled delivery (future type only) */}
              {letterType === 'future' && (
                <div>
                  <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--opp-accent-strong))]">
                    <AlarmClock className="h-3.5 w-3.5" />
                    {t('write.schedule.label')}
                  </label>
                  <OppInput
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date(Date.now() + ONE_DAY_MS).toISOString().split('T')[0]}
                    className="opp-write-input w-full rounded-xl border-2 border-[hsl(var(--opp-border))] px-4 py-2.5 text-sm focus:border-[hsl(var(--opp-accent))] focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-[hsl(var(--opp-muted))]">{t('write.schedule.hint')}</p>
                </div>
              )}

              {/* Anonymous toggle */}
              <div className="flex items-center justify-between py-3 border-t border-[hsl(var(--opp-border))]">
                <div>
                  <p className="text-sm font-medium text-[hsl(var(--opp-ink))]">{t('write.anonymous.label')}</p>
                  <p className="text-xs text-[hsl(var(--opp-accent-strong))]">{t('write.anonymous.hint')}</p>
                </div>
                <OppButton
                  data-opp-anonymous-toggle
                  type="button"
                  onClick={() => setIsAnonymous(v => !v)}
                  className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                  aria-pressed={isAnonymous}
                  aria-label={t('write.anonymous.label')}
                >
                  <span
                    data-opp-toggle-track
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      isAnonymous ? 'bg-[hsl(var(--opp-accent))]' : 'bg-[hsl(var(--opp-accent-soft))]'
                    }`}
                  >
                    <span
                      data-opp-toggle-thumb
                      className={`inline-block h-4 w-4 transform rounded-full transition-[transform,background-color] ${
                        isAnonymous
                          ? 'translate-x-6 bg-[hsl(var(--opp-control-thumb-active))]'
                          : 'translate-x-1 bg-[hsl(var(--opp-control-thumb-inactive))]'
                      }`}
                    />
                  </span>
                </OppButton>
              </div>
            </div>
          )}
        </div>

        {/* Desktop action buttons */}
        <div data-opp-write-desktop-actions className="hidden md:flex items-center justify-end gap-3">
          <OppButton
            onClick={handleSaveDraft}
            disabled={createLetter.isPending || validating || !content.trim()}
            variant="outline"
            className="flex min-h-11 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-40"
          >
            <Save className="h-4 w-4" />
            {t('write.btn.draft')}
          </OppButton>
          <OppButton
            onClick={handleSend}
            disabled={createLetter.isPending || validating || !content.trim()}
            variant="primary"
            className="flex min-h-11 items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold shadow-md disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
            {t('write.btn.send')}
          </OppButton>
        </div>

        {actionFeedback && <SemanticFeedback kind={actionFeedback.kind} title={actionFeedback.title} className="mt-3">{actionFeedback.description}</SemanticFeedback>}

        {createLetter.isError && (
          <SemanticState compact kind="error" illustration="state-error-network" title={t('write.send.errorRetry')} action={<OppButton onClick={handleSend} disabled={createLetter.isPending || validating} variant="primary" className="min-h-11 rounded-full px-4 text-xs font-semibold">重试发送</OppButton>} />
        )}
      </div>

      {/* ── DRAFTS SIDEBAR (desktop) ── */}
      {sidebarOpen && (
        <aside className="opp-write-panel hidden md:flex flex-col w-72 shrink-0 border-l border-[hsl(var(--opp-border))] pt-2 pb-8 px-4 gap-3">
          <div className="flex items-center justify-between py-2">
            <h3 className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{t('write.nav.drafts')}</h3>
            <Link to="/letters" className="text-xs text-[hsl(var(--opp-muted))] hover:text-[hsl(var(--opp-accent-strong))] underline underline-offset-2">{t('write.draft.viewAll')}</Link>
          </div>
          {!drafts || drafts.length === 0 ? (
            <SemanticState compact illustration="empty-draft" kind="empty" title={t('write.draft.empty')} />
          ) : (
            <div className="space-y-2 overflow-y-auto">
              {drafts.map((draft: OppLetter) => (
                <OppButton
                  key={draft.id}
                  onClick={() => {
                    setDraftId(draft.id);
                    if (draft.content) setContent(draft.content);
                    if (draft.title) setTitle(draft.title);
                    if (draft.style) setStyle(draft.style as StyleId);
                    if (draft.type) setLetterType(draft.type as LetterType);
                    if (draft.metadata && typeof draft.metadata.delivery_mode === 'string' && (draft.metadata.delivery_mode === 'online' || draft.metadata.delivery_mode === 'offline')) setDeliveryMode(draft.metadata.delivery_mode as DeliveryMode);
                    if (draft.recipient_op_code) setRecipientCode(draft.recipient_op_code);
                    if (draft.scheduled_at) setScheduledDate(draft.scheduled_at.slice(0, 10));
                    if (draft.envelope_id) setSelectedEnvelopeId(draft.envelope_id);
                    setIsAnonymous(draft.is_anonymous ?? false);
                  }}
                  className="w-full rounded-xl border border-[hsl(var(--opp-border))] bg-letter-paper p-3 text-left hover:border-[hsl(var(--opp-border))] hover:shadow-sm transition-all"
                >
                  <p className="text-xs font-semibold text-[hsl(var(--opp-ink))] truncate">
                    {draft.title || t('write.draft.untitled')}
                  </p>
                  <p className="text-caption text-[hsl(var(--opp-muted))] mt-0.5">
                    {draft.created_at
                      ? new Date(draft.created_at).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
                      : '—'}
                  </p>
                </OppButton>
              ))}
            </div>
          )}
        </aside>
      )}

      {/* ── MOBILE FIXED BOTTOM ACTION BAR ── */}
      <div
        data-opp-write-actions
        className="opp-material md:hidden fixed left-0 right-0 z-40 flex items-center gap-2 border-y border-[hsl(var(--opp-border))] px-4 py-3 shadow-opp-navigation"
        style={{ bottom: 'calc(var(--opp-tabbar-height) + env(safe-area-inset-bottom) + 2px)' }}
      >
        <OppButton
          onClick={handleSaveDraft}
          disabled={createLetter.isPending || validating || !content.trim()}
          variant="outline"
          className="flex min-h-11 w-[var(--litopia-layout-30-pct)] items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
        >
          <Save className="h-4 w-4" />
          {t('write.btn.draft')}
        </OppButton>
        <OppButton
          onClick={handleSend}
          disabled={createLetter.isPending || validating || !content.trim()}
          variant="primary"
          className="flex min-h-11 w-[var(--litopia-layout-70-pct)] items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
        >
          <Send className="h-4 w-4" />
          {t('write.btn.send')}
        </OppButton>
      </div>
    </div>
  );
}
